import CustomTable from "@/components/CustomTable/CustomTable";
import { Loader } from "@/components/Loader/Loader";
import { tableau_logs } from "@/config/initData";
import { getAllUsersLogs } from "@/features/auth/api/user.api";
import { TUser } from "@/features/auth/types";
import {
    Box,
    Grid,
    useMediaQuery,
    useTheme
} from "@mui/material";
import React, { useEffect, useState } from "react";
import PerfectScrollbar from "react-perfect-scrollbar";
import styled from "styled-components";
import "../Users-content/Users.content.style.scss"; // Réutilisation du style existant

const UsersLogsContent: React.FC = () => {
    // Les variable de responsivité
    const theme = useTheme();
    const matches_down_xl = useMediaQuery(theme.breakpoints.down("xl"));
    const matches_down_sm = useMediaQuery(theme.breakpoints.down("sm"));

    const [isLoading, setIsLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [usersLogsData, setUsersLogsData] = useState<any[]>([]);
    const [page, setPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const limit = matches_down_xl ? 8 : 10;

    // Récupération des logs utilisateurs
    const getUsersLogsList = async (pageToFetch: number, search?: string) => {
        setIsLoading(true);
        try {
            const response = await getAllUsersLogs(pageToFetch, limit, search);
            console.log("USERS LOGS RESPONSE:", response);
            const data = response?.data || [];

            // Set total count from pagination metadata if available
            if (response?.pagination?.total) {
                setTotalCount(response.pagination.total);
            } else {
                setTotalCount(0);
            }

            // Formatage des données pour le tableau
            const formattedData = data.map((log: any) => ({
                date: new Date(log.last_login_date).toLocaleString('fr-FR'), // Formatage date
                nom: log.user_id?.nom || 'Inconnu',
                prenoms: log.user_id?.prenoms || 'Inconnu',
                email: log.user_id?.email || 'Inconnu',
            }));

            setUsersLogsData(formattedData);
        } catch (error) {
            console.error("Erreur lors de la récupération des logs", error);
            setUsersLogsData([]);
            setTotalCount(0);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        getUsersLogsList(page, searchTerm);
    }, [page, limit]); // Removed searchTerm from dependency to avoid double call on debounce, see below

    // Handle page change
    const handleChangePage = (event: any, newPage: number) => {
        setPage(newPage);
    };

    // Debounce search
    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            if (page === 1) {
                getUsersLogsList(1, searchTerm);
            } else {
                setPage(1); // will trigger useEffect above
            }
        }, 500);

        return () => clearTimeout(delayDebounceFn);
    }, [searchTerm]);

    // Function to handle search input
    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(event.target.value);
    };

    // No longer filter client-side since API does it
    const filteredData = usersLogsData;

    // Function to handle export all data
    const handleExportAll = async (): Promise<any[]> => {
        try {
            // Using -1 as limit to fetch all records (backend support required)
            // Or use a very large number if -1 is not supported, but we updated backend to support -1
            const response = await getAllUsersLogs(1, -1);
            const data = response?.data || [];

            // Formatage des données pour le tableau (same formatting as list)
            return data.map((log: any) => ({
                date: new Date(log.last_login_date).toLocaleString('fr-FR'),
                nom: log.user_id?.nom || 'Inconnu',
                prenoms: log.user_id?.prenoms || 'Inconnu',
                email: log.user_id?.email || 'Inconnu',
            }));
        } catch (error) {
            console.error("Error exporting all logs", error);
            return [];
        }
    };

    return (
        <>
            <PerfectScrollbar className="ct-home">
                <Box>
                    <Grid
                        container
                        rowSpacing={2}
                        columnSpacing={2}
                        justifyContent="flex-end"
                    >
                        {/* ====== ROW ====== */}
                        <>
                            <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                                <DisplayContentAlt height={"80px"}>
                                    <h2 className="uppercase"> Historique des connexions</h2>
                                </DisplayContentAlt>
                            </Grid>
                        </>
                        {/* ====== end of a ROW ====== */}

                        {/* ====== ROW ====== */}
                        <>
                            <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                                <DisplayContent
                                    height={
                                        matches_down_xl
                                            ? matches_down_sm
                                                ? "fit-content"
                                                : "95vh"
                                            : "95vh"
                                    }
                                >
                                    <Loader isLoading={isLoading} />

                                    <CustomTable
                                        headers={tableau_logs.headers}
                                        rows={filteredData}
                                        title={tableau_logs.title}
                                        headerColor="var(--ui-tertiary)"
                                        textColor="var(--ui-white)"
                                        titleExport={tableau_logs.titleExport}
                                        colsToExport={tableau_logs.colsToExport}
                                        rowsPerPage={limit}
                                        searchTerm={searchTerm}
                                        handleSearchChange={handleSearchChange}
                                        table="users" // Pour activer la recherche
                                        page={page}
                                        totalCount={totalCount}
                                        onPageChange={handleChangePage}
                                        onExport={handleExportAll}
                                    />
                                </DisplayContent>
                            </Grid>
                        </>
                        {/* ====== end of a ROW ====== */}
                    </Grid>
                </Box>
            </PerfectScrollbar>
        </>
    );
};

const DisplayContent = styled.div<{ height: string }>`
  width: 100%;
  height: ${({ height }) => {
        return height;
    }};
  border-radius: 5px;
  background: var(--ui-white);
  box-shadow: 0px 0px 30px #ededed;
`;

const DisplayContentAlt = styled.div<{ height: string }>`
  width: 100%;
  height: ${({ height }) => {
        return height;
    }};
  border-radius: 5px;
  display: flex;
  align-items: center;

  h2 {
    color: var(--ui-dark);
  }
`;

export default UsersLogsContent;
